import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import { formatRoute } from 'react-router-named-routes';
import { useNavigate } from 'react-router-dom';
import AddToPlannerModal from '../meal/add_to_planner_modal';
import { copyMealToPlanner } from '../../operations/planner_operations';
import RecipesEndUserMealCard from './recipes_end_user_meal_card';
import {
  scaleMeal,
  scaleMealIngredientsOperation,
} from '../../operations/meal_operations';
import FullWidthMealCard from '../meal/full_width_meal_card';
import EndUserOrganiseRecipeModal from '../meal/end_user_organise_recipe_modal';
import {
  END_USER_RECIPES_BOARD_MEAL_ROUTE,
  END_USER_RECIPES_BOARD_ROUTE,
} from '../../services/routes';
import {
  mealFromSharedMeal,
  mealWithParentUpdated,
  mealWithScaledIngredientsReplaced,
  recipeCollectionsCardDOMID,
} from '../../services/meals';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import {
  useCreateMealMutation,
  useDeleteMealMutation,
  useLazyGetFullMealQuery,
  useLazyGetFullSharedMealQuery,
  useLazyGetSmorgBoardQuery,
  useUpdateDbPlannerWeekOptimisticallyMutation,
  useUpdateMealMutation,
  useUpdateSmorgBoardMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  boardWithMealAdded,
  boardWithMealMoved,
  boardWithMealRemoved,
} from '../../services/smorg_board';
import {
  mealsAddedToMealBasket,
  mealsRemovedFromMealBasket,
} from '../../features/end_user_shopping/end_user_shopping_slice';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import { trackEvents, useTracker } from '../../operations/tracking_operations';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetDistilledMealByIDAndParentQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetDistilledSharedMealByIDAndParentQuery,
  useGetUserProfileQuery,
  useLazyGetOrCreateDbPlannerWeekByStartDateQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  copyMealToBoard,
  copySharedMealToBoard,
} from '../../operations/recipes_operations';
import RecipePlaceholderCard from '../meal/recipe_placeholder_card';
import { isMealBackgroundProcessingSelector } from '../../features/recipes/recipes_slice';
import DeleteCardConfirmPopover from '../common/delete_card_confirm_popover';

const RecipesEndUserMealItem = ({
  mealID,
  menuID,
  parentID,
  favouriteEnabled,
  copyEnabled,
  deleteEnabled,
  isFullWidth,
}) => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const tracker = useTracker();

  const isBackgroundProcessing = useSelector((state) =>
    isMealBackgroundProcessingSelector(state, mealID),
  );

  const { data: currentSpaceMembership, isLoading: spaceMembershipLoading } =
    useGetCurrentSpaceMembershipQuery();

  const {
    data: distilledMealInMeals,
    isLoading: isDistilledMealInMealsLoading,
  } = useGetDistilledMealByIDAndParentQuery({ id: mealID, parentID });

  const {
    data: distilledMealInSharedMeals,
    isLoading: isDistilledMealInSharedMealsLoading,
  } = useGetDistilledSharedMealByIDAndParentQuery({ id: mealID, parentID });

  const [lazyGetSmorgBoard] = useLazyGetSmorgBoardQuery();

  const distilledMeal = distilledMealInMeals || distilledMealInSharedMeals;

  const isSharedMeal = !!distilledMealInSharedMeals;

  const [addToPlannerAnchorEl, setAddToPlannerAnchorEl] = useState(null);

  const [copyToBoardAnchorEl, setCopyToBoardAnchorEl] = useState(null);

  const onClickAddToPlanner = (target) => {
    setAddToPlannerAnchorEl(target);
  };

  const { data: userProfile } = useGetUserProfileQuery();

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const parentBoard = (ownedRecipesBoards || []).find((b) => b.id === parentID);

  const [lazyGetOrCreateDbPlannerWeekByStartDate] =
    useLazyGetOrCreateDbPlannerWeekByStartDateQuery();

  const [createMeal] = useCreateMealMutation();

  const [updateMeal] = useUpdateMealMutation();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekOptimisticallyMutation();

  const [deleteMeal] = useDeleteMealMutation();

  const [updateSmorgBoard] = useUpdateSmorgBoardMutation();

  const [isAddingMealsToBasket, setIsAddingMealsToBasket] = useState(false);

  const isLoading =
    isDistilledMealInSharedMealsLoading ||
    spaceMembershipLoading ||
    isDistilledMealInMealsLoading;

  // const hasLoadedWithError =
  //   isDistilledMealInMealsError || isDistilledMealInSharedMealsError;

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetSharedMeal] = useLazyGetFullSharedMealQuery();

  const afterAddingToFavourites = useCallback(
    ({ favouritesRecipesBoardID }) => {
      //       const cb = (copiedMeal) => {
      //   window.setTimeout(() => {
      //     const el = document.getElementById(
      //       recipeCollectionsCardDOMID(copiedMeal.id),
      //     );
      //     if (el) {
      //       el.scrollIntoView({ behavior: 'smooth' });
      //     }
      //   }, 200);
      // };

      dispatch(
        showGlobalSnackbar({
          notificationText: 'Your meal has been added to your favourites',
          linkText: 'View favourites',
          linkTarget: formatRoute(END_USER_RECIPES_BOARD_ROUTE, {
            recipesBoardId: favouritesRecipesBoardID,
          }),
        }),
      );
    },
    [dispatch],
  );

  const onCopyToPlanner = useCallback(
    async (plannerViewWeekStartDate, dayIndexes, scaleToServings) => {
      let fullMealToCopy;
      if (isSharedMeal) {
        const { data: fullSharedMeal } = await lazyGetSharedMeal(mealID);
        fullMealToCopy = mealFromSharedMeal(fullSharedMeal);
      } else {
        const { data: fullMeal } = await lazyGetMeal(mealID);
        fullMealToCopy = fullMeal;
      }
      copyMealToPlanner(
        fullMealToCopy,
        plannerViewWeekStartDate,
        dayIndexes,
        customerFoodDataID,
        tracker,
        userProfile,
        currentSpaceMembership,
        lazyGetOrCreateDbPlannerWeekByStartDate,
        createMeal,
        updateMeal,
        updateDbPlannerWeek,
        lazyGetMeal,
        scaleToServings,
      );
    },
    [
      isSharedMeal,
      customerFoodDataID,
      tracker,
      userProfile,
      currentSpaceMembership,
      lazyGetOrCreateDbPlannerWeekByStartDate,
      createMeal,
      updateMeal,
      updateDbPlannerWeek,
      lazyGetSharedMeal,
      mealID,
      lazyGetMeal,
    ],
  );

  const isInMealBasket = useSelector((state) =>
    state.endUserShopping.mealBasketItems.some(
      (item) => item.meal.id === mealID,
    ),
  );

  const onAddToMealBasket = useCallback(
    async (scaleToServings) => {
      setIsAddingMealsToBasket(true);
      try {
        trackEvents([
          { name: 'Meals added to basket', args: { source: 'recipes' } },
        ]);
        let fullMeal;
        if (isSharedMeal) {
          const { data: fullSharedMeal } = await lazyGetSharedMeal(mealID);
          fullMeal = mealFromSharedMeal(fullSharedMeal);
          fullMeal.id = mealID;
        } else {
          const { data } = await lazyGetMeal(mealID);
          fullMeal = data;
        }
        let scaledMeal;
        if (scaleToServings) {
          const scaleFactor =
            scaleToServings / (fullMeal.recipes[0]?.servings || 1);
          const scaledIngredients = await scaleMealIngredientsOperation(
            fullMeal,
            scaleFactor,
            customerFoodDataID,
          );
          scaledMeal = mealWithScaledIngredientsReplaced(
            fullMeal,
            scaleToServings,
            scaledIngredients,
            scaleFactor,
          );
        } else {
          scaledMeal = fullMeal;
        }
        dispatch(mealsAddedToMealBasket([scaledMeal]));
      } finally {
        setIsAddingMealsToBasket(false);
      }
    },
    [
      customerFoodDataID,
      dispatch,
      isSharedMeal,
      lazyGetMeal,
      lazyGetSharedMeal,
      mealID,
    ],
  );

  const onRemoveFromMealBasket = useCallback(() => {
    dispatch(mealsRemovedFromMealBasket([mealID]));
  }, [dispatch, mealID]);

  const [deleteMealAnchorEl, setDeleteMealAnchorEl] = useState(null);

  const onClickDelete = useCallback((ev) => {
    setDeleteMealAnchorEl(ev.target);
    ev.stopPropagation();
  }, []);

  const onDelete = useCallback(async () => {
    dispatch(mealsRemovedFromMealBasket([mealID]));
    const updatedParentBoard = boardWithMealRemoved(
      parentBoard,
      mealID,
      menuID,
    );
    return Promise.all([
      deleteMeal({
        id: mealID,
        parentID,
      }),
      updateSmorgBoard(updatedParentBoard),
    ]);
  }, [
    deleteMeal,
    dispatch,
    mealID,
    menuID,
    parentBoard,
    parentID,
    updateSmorgBoard,
  ]);

  const onClick = useCallback(() => {
    navigate(
      formatRoute(END_USER_RECIPES_BOARD_MEAL_ROUTE, {
        recipesBoardId: parentID,
        mealId: mealID,
      }),
    );
  }, [mealID, navigate, parentID]);

  const calories =
    distilledMeal?.derivedNutrition?.totalNutritionPerServing?.calories;

  const servings = distilledMeal?.recipes[0]?.servings;

  const [scalingInProgress, setScalingInProgress] = useState(false);

  const onChangeScaleToServings = useCallback(
    async (scaleToServings) => {
      const scaleToServingsNumber = Number(scaleToServings) || 1;
      if (scaleToServingsNumber !== servings) {
        const scaleFactor = scaleToServingsNumber / (servings || 1);
        setScalingInProgress(true);
        try {
          // if (isSharedMeal) {
          //   await dispatch(
          //     locallyScaleSharedMealAction(
          //       mealID,
          //       scaleFactor,
          //       scaleToServingsNumber,
          //     ),
          //   );
          // } else {
          const { data: fullMeal } = await lazyGetMeal(mealID);
          await scaleMeal(
            fullMeal,
            scaleFactor,
            scaleToServingsNumber,
            customerFoodDataID,
            false,
            updateMeal,
            dispatch,
            lazyGetMeal,
          );
          // }
        } finally {
          setScalingInProgress(false);
        }
      }
    },
    [customerFoodDataID, dispatch, lazyGetMeal, mealID, servings, updateMeal],
  );

  const onClickCopyToRecipesBoard = useCallback((target) => {
    setCopyToBoardAnchorEl(target);
  }, []);

  const onCopyToRecipesBoard = useCallback(
    async (toRecipesBoardId, toMenuId) => {
      const { data: destinationRecipesBoard } = await lazyGetSmorgBoard(
        toRecipesBoardId,
        true,
      );
      const cb = (copiedMeal) => {
        window.setTimeout(() => {
          const el = document.getElementById(
            recipeCollectionsCardDOMID(copiedMeal.id),
          );
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
          }
        }, 200);
      };

      if (isSharedMeal) {
        const { data: fullSharedMeal } = await lazyGetSharedMeal(mealID);
        await copySharedMealToBoard(
          fullSharedMeal,
          destinationRecipesBoard,
          toMenuId,
          currentSpaceMembership,
          updateSmorgBoard,
          createMeal,
          userProfile,
          tracker,
          null,
          cb,
          dispatch,
        );
      } else {
        const { data: fullMeal } = await lazyGetMeal(mealID);
        await copyMealToBoard(
          fullMeal,
          destinationRecipesBoard,
          toMenuId,
          currentSpaceMembership,
          updateSmorgBoard,
          createMeal,
          userProfile,
          tracker,
          null,
          cb,
          dispatch,
          null,
        );
      }
    },
    [
      lazyGetSmorgBoard,
      isSharedMeal,
      lazyGetSharedMeal,
      mealID,
      currentSpaceMembership,
      updateSmorgBoard,
      createMeal,
      userProfile,
      tracker,
      dispatch,
      lazyGetMeal,
    ],
  );

  const onMoveToRecipesBoard = useCallback(
    async (toRecipesBoardId, toMenuId) => {
      if (parentID === toRecipesBoardId) {
        const newBoard = boardWithMealMoved(
          parentBoard,
          mealID,
          menuID,
          toMenuId,
        );
        await updateSmorgBoard(newBoard);
        return;
      }
      const { data: destinationRecipesBoard } = await lazyGetSmorgBoard(
        toRecipesBoardId,
        true,
      );
      const { data: fullMeal } = await lazyGetMeal(mealID);
      const updatedMeal = mealWithParentUpdated(fullMeal, toRecipesBoardId);
      await updateMeal(updatedMeal);
      const newSourceRecipesBoard = boardWithMealRemoved(
        parentBoard,
        mealID,
        menuID,
      );
      const newDestinationRecipesBoard = boardWithMealAdded(
        destinationRecipesBoard,
        toMenuId,
        { id: mealID },
      );
      await Promise.all(
        [newSourceRecipesBoard, newDestinationRecipesBoard].map((board) =>
          updateSmorgBoard(board),
        ),
      );
    },
    [
      lazyGetMeal,
      lazyGetSmorgBoard,
      mealID,
      menuID,
      parentBoard,
      parentID,
      updateMeal,
      updateSmorgBoard,
    ],
  );

  if (isLoading) {
    return <RecipePlaceholderCard />;
  }

  if (!distilledMeal) {
    return null;
  }

  return (
    <>
      {isFullWidth ? (
        <FullWidthMealCard
          mealID={mealID}
          parentID={parentID}
          title={distilledMeal.recipes[0].title}
          imageUrl={distilledMeal.recipes[0].imageUrl}
          mealTypes={distilledMeal.recipes[0].mealTypes}
          calories={calories}
          servings={servings}
          onClick={onClick}
          servingsEnabled
          onChangeScaleToServings={onChangeScaleToServings}
          addToPlannerEnabled
          copyEnabled={copyEnabled}
          shoppingEnabled
          favouriteEnabled={favouriteEnabled}
          deleteEnabled={deleteEnabled}
          isInMealBasket={isInMealBasket}
          isAddingMealsToBasket={isAddingMealsToBasket}
          onClickAddToMealBasket={onAddToMealBasket}
          onClickRemoveFromMealBasket={onRemoveFromMealBasket}
          onClickCopyToPlanner={onClickAddToPlanner}
          onClickCopyToRecipesBoard={onClickCopyToRecipesBoard}
          onClickDelete={onClickDelete}
          onDelete={onDelete}
          scalingInProgress={scalingInProgress}
          afterAddingToFavourites={afterAddingToFavourites}
          isBackgroundProcessing={isBackgroundProcessing}
        />
      ) : (
        <RecipesEndUserMealCard
          mealID={mealID}
          parentID={parentID}
          title={distilledMeal.recipes[0].title}
          imageUrl={distilledMeal.recipes[0].imageUrl}
          favouriteEnabled={favouriteEnabled}
          deleteEnabled={deleteEnabled}
          isInMealBasket={isInMealBasket}
          isAddingMealsToBasket={isAddingMealsToBasket}
          onClick={onClick}
          onAddToMealBasket={onAddToMealBasket}
          onRemoveFromMealBasket={onRemoveFromMealBasket}
          onAddToPlanner={onClickAddToPlanner}
          onDelete={onDelete}
          afterAddingToFavourites={afterAddingToFavourites}
          isBackgroundProcessing={isBackgroundProcessing}
        />
      )}
      {addToPlannerAnchorEl && (
        <AddToPlannerModal
          anchorEl={addToPlannerAnchorEl}
          copyToPlannerEnabled
          editServingsEnabled
          titleText="Schedule"
          onCopyToPlanner={(
            plannerViewWeekStartDate,
            dayIndexes,
            scaleToServings,
          ) => {
            onCopyToPlanner(
              plannerViewWeekStartDate,
              dayIndexes,
              scaleToServings,
            );
            setAddToPlannerAnchorEl(null);
            dispatch(
              showGlobalSnackbar({
                notificationText: 'The meal has been copied to your planner',
                linkText: 'View planner',
                linkTarget: `/planner/${plannerViewWeekStartDate}`,
              }),
            );
          }}
          onDismiss={() => setAddToPlannerAnchorEl(null)}
        />
      )}
      {copyToBoardAnchorEl && (
        <EndUserOrganiseRecipeModal
          recipesBoardId={parentID}
          menuSelectionEnabled
          anchorEl={copyToBoardAnchorEl}
          onCopy={(toRecipesBoardId, toMenuId) => {
            onCopyToRecipesBoard(toRecipesBoardId, toMenuId);
            setCopyToBoardAnchorEl(null);
            dispatch(
              showGlobalSnackbar({
                notificationText: 'Your meal has been copied to your board',
                linkText: 'View board',
                linkTarget: formatRoute(END_USER_RECIPES_BOARD_ROUTE, {
                  recipesBoardId: toRecipesBoardId,
                }),
              }),
            );
          }}
          isMoveEnabled={!isSharedMeal}
          onMove={(toRecipesBoardId, toMenuId) => {
            onMoveToRecipesBoard(toRecipesBoardId, toMenuId);
            setCopyToBoardAnchorEl(null);
            dispatch(
              showGlobalSnackbar({
                notificationText: 'Your meal has been moved to your board',
                linkText: 'View board',
                linkTarget: formatRoute(END_USER_RECIPES_BOARD_ROUTE, {
                  recipesBoardId: toRecipesBoardId,
                }),
              }),
            );
          }}
          onDismiss={() => setCopyToBoardAnchorEl(null)}
          boardTitle="collection"
        />
      )}
      <DeleteCardConfirmPopover
        visible={!!deleteMealAnchorEl}
        anchorEl={deleteMealAnchorEl}
        title="Delete meal"
        message="The meal will be deleted permanently. Are you sure?"
        height={140}
        onConfirmDelete={onDelete}
        onDismiss={() => setDeleteMealAnchorEl(null)}
      />
    </>
  );
};

RecipesEndUserMealItem.propTypes = {
  mealID: PropTypes.string.isRequired,
  menuID: PropTypes.string.isRequired,
  parentID: PropTypes.string,
  favouriteEnabled: PropTypes.bool.isRequired,
  copyEnabled: PropTypes.bool,
  deleteEnabled: PropTypes.bool.isRequired,
  isFullWidth: PropTypes.bool,
};

RecipesEndUserMealItem.defaultProps = {
  parentID: null,
  copyEnabled: false,
  isFullWidth: false,
};

export default RecipesEndUserMealItem;
